import { Injectable } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { DetectDevicesService, PostService } from '@fi-shared/fi-shared-services';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { NewsFeedModel } from './news-feed.model';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ReplaySubject, Subject } from 'rxjs';
import { INewsFeedData } from '@fi-app/news-feed/news-feed/news-feed.component';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { FiNewsFeedSharedService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { INewsFeedModel } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiItemUpdatedEventType } from '@fi-shared/fi-signal-r/fi-item-update-event-type';
import { ActionType } from '@fi-shared/types';
import { FiForumSharedService } from '@fi-shared/fi-forum/fi-forum-shared.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { IObjectDto } from '@fi-shared/models/models';

interface INewNewsFeedsChanged {
    forumId: number;
    delta: number;
}

@Injectable()
export class NewsFeedService {
    newNewsFeedsChanged$ = new Subject<INewNewsFeedsChanged>();

    constructor(
        private fiBackend: FiBackendService,
        private detectDevices: DetectDevicesService,
        private forumSharedService: FiForumSharedService,
        private postService: PostService,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private fiSignalRService: FiSignalRService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiLoading: LoadingService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private userDataService: FiUserDataService,
        private newsFeedSharedService: FiNewsFeedSharedService,
    ) {}

    Initialize() {
        this.fiSignalRService.init({ isMobileApp: false });

        this.fiSignalRService.itemUpdatedEvent.subscribe((newsItem) => {
            if (
                newsItem.EventType === FiItemUpdatedEventType.Feed &&
                newsItem.EventByUserId !== this.userDataService.getUserId() &&
                !this.isNewsItemObjectTypeIgnored(newsItem.ObjectType)
            ) {
                this.dispatchNewNewsFeedsChangedEvent({
                    forumId: newsItem.ForumId,
                    delta: newsItem.ActionType !== ActionType.Delete ? 1 : -1,
                });
            }
        });

        /*// crash test
        this.fiSignalRService.itemUpdatedEvent.subscribe(data => {
            if (data.EventType === FiItemUpdatedEventType.Feed &&
                data.ActionType === ActionType.NotificationCategoryChanged)
            {
                this.fiBackend.fetchDataPost<any>('api2/Feed/GetFeedDataByIdType', [ {
                    ObjectId: data.ObjectId,
                    ObjectType: data.ObjectType,
                }], {
                    supressErrorMessage: true
                }).then(result => {
                });
            }
        });*/
    }

    private readonly ignoredObjectTypes: ObjectTypes[] = [
        ObjectTypes.User,
        ObjectTypes.CheckPointAnswerNotification,
        ObjectTypes.CheckListActivated,
        ObjectTypes.CheckListReminder,
        ObjectTypes.CheckList,
    ];
    isNewsItemObjectTypeIgnored(objectType: ObjectTypes) {
        return this.ignoredObjectTypes.includes(objectType);
    }

    private dispatchNewNewsFeedsChangedEvent(data: INewNewsFeedsChanged) {
        this.newNewsFeedsChanged$.next(data);
    }

    OnMarkNewsFeedAsVisited(item: NewsFeedModel) {
        this.dispatchNewNewsFeedsChangedEvent({
            forumId: item.ForumId,
            delta: -1,
        });
        this.fiSignalRHelperService.onMarkNewsFeedAsVisited(item);
        this.mobileAppHelperService.onMarkNewsFeedAsVisited(item);
    }

    getDefaultNumberOfRecords() {
        return this.detectDevices.isMobileSmall() ? 10 : FiAppSettings.NumberOfRecordsPerPage;
    }

    private getParameterApiModel(offset: number, newsFeedData: INewsFeedData) {
        const result = {
            Offset: offset,
            NumberOfRecordsToTake: newsFeedData.NumberOfRecordsToTake || this.getDefaultNumberOfRecords(),
            OnlyUnvisited: this.stateService.params.isOnlyUnvisited,
            ShowForumActivity: newsFeedData.showForumActivity,
            IsForumMode: undefined,
            ForumIds: undefined,
            ContentSearchTexts: undefined,
            ShowAllForumPostsIfAdmin: newsFeedData.showAllForumPostsIfAdmin,
        };
        if (newsFeedData.forumId) {
            // load news feed for forum
            result.IsForumMode = true;
            result.ForumIds = [newsFeedData.forumId];
        }
        return result;
    }

    GetNewsFeeds(offset: number, newsFeedData: INewsFeedData) {
        /*let url2 = `api2/Feed/GetSocialFeedData/${offset}/${30}`; // todo for flutter app tests
            this.fiBackend.fetchData<any[]>(url2).then(data => {
                console.log('api2 GetSocialFeedData', data);
            })*/

        /*let url2 = `api2/Feed/GetPriorityFeedData/${offset}/${30}`; // todo for flutter app tests
            this.fiBackend.fetchData<any[]>(url2).then(data => {
                console.log('api2 GetPriorityFeedData', data);
            })*/

        const parameter = this.getParameterApiModel(offset, newsFeedData);
        return this.fiBackend.fetchDataPost<INewsFeedModel[]>('NewsFeed/NewsFeed/GetNewsFeeds', parameter);
    }

    getForumFeedData(forumId: number, offset: number, numberOfRecordsToTake: number) {
        return this.fiBackend.fetchData<{
            forumName: string;
            data: any[];
        }>('NewsFeed/NewsFeed/GetForumFeedData/' + forumId + '/' + offset + '/' + numberOfRecordsToTake);
    }

    GetNewsFeedsToPost(objectId, objectType, forumId?) {
        let url = `NewsFeed/NewsFeed/GetNewsFeedsToPost/${objectId}/${objectType}/${FiAppSettings.NumberOfRecordsPerPage}`;
        if (forumId) {
            url += `/${forumId}`;
        }
        return this.fiBackend.fetchData<INewsFeedModel[]>(url);
    }

    GetNewsFeedsByIdType(objectIdTypes: IObjectDto[]) {
        return this.fiBackend.fetchDataPost<INewsFeedModel[]>('NewsFeed/NewsFeed/GetNewsFeedsByIdType', objectIdTypes);
    }

    GetNewsFeedsByGroupId(groupId) {
        return this.fiBackend.fetchData<INewsFeedModel[]>('NewsFeed/NewsFeed/GetNewsFeedsByGroupId/' + groupId);
    }

    RemoveItemFromNewsFeed(objectId, objectType) {
        return this.fiBackend.fetchDataPost(`NewsFeed/NewsFeed/RemoveItemFromNewsFeed/${objectId}/${objectType}`);
    }

    ShowComment = false;

    deleteNewsFeedItem(itemToDelete: NewsFeedModel): Promise<void> {
        const promise = new Promise<void>((resolve, reject) => {
            if (itemToDelete) {
                this.fiDialogsService
                    .confirm({
                        messageText: this.appTextService.get('post.confirm_delete_message'),
                        headerText: this.appTextService.get('dialog.header_confirm'),
                    })
                    .onYesButtonClick(() => {
                        switch (itemToDelete.ObjectType) {
                            case ObjectTypes.Post:
                                this.postService.deletePost(itemToDelete.ObjectId).then(resolve, reject);
                                break;
                            case ObjectTypes.ForumPost:
                                this.forumSharedService.deleteForumPosts([itemToDelete.ObjectId]).then(resolve, reject);
                                break;
                            default:
                                const promises = [];
                                this.newsFeedSharedService.getObjectItems([itemToDelete]).forEach((item) => {
                                    promises.push(this.RemoveItemFromNewsFeed(item.ObjectId, item.ObjectType));
                                });
                                this.fiLoading.trackPromise(Promise.all(promises)).then(() => resolve(), reject);
                        }
                    })
                    .onNoButtonClick(() => reject());
            } else {
                reject();
            }
        });
        if ([ObjectTypes.Post, ObjectTypes.ForumPost].includes(itemToDelete.ObjectType)) {
            promise.then(() => {
                this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                    itemToDelete.ObjectType,
                    [itemToDelete.ObjectId],
                    undefined,
                );
            });
        }
        return promise;
    }

    private _loadNewNewsFeedItems$ = new ReplaySubject<void>(1);
    get loadNewNewsFeedItems$() {
        return this._loadNewNewsFeedItems$.asObservable();
    }
    raiseLoadNewNewsFeedItems() {
        this._loadNewNewsFeedItems$.next();
    }

    private _homeTabScrolled$ = new ReplaySubject<void>(1);
    get homeTabScrolled$() {
        return this._homeTabScrolled$.asObservable();
    }

    raiseHomeTabScrolled() {
        return this._homeTabScrolled$.next();
    }

    /*updateDeleteAccess(items: NewsFeedModel[]) {
        //const objectItems = NewsFeedModel.getObjectItems(items);
        this.accessRightService.GetEditAccessData(items).then((editAccessData) => {
            items.forEach(newsFeedItem =>  {
                /!*const currentObjectItems = newsFeedItem.getObjectItems();
                const editAccessItems = editAccessData.filter((editAccessItem) => {
                    const currentObjectItem = currentObjectItems.find(item => item.ObjectId == editAccessItem.ObjectId && item.ObjectType == editAccessItem.ObjectType);
                    return !!currentObjectItem;
                });*!/
                const editAccessItem = editAccessData.find(x => x.ObjectId === newsFeedItem.ObjectId && x.ObjectType === newsFeedItem.ObjectType);
                if (editAccessItem) {
                    newsFeedItem.HasEditAccess = editAccessItem.HasEditAccess;
                    newsFeedItem.HasDeleteAccess = editAccessItem.HasDeleteAccess;
                }
            });
        });
    }*/
}
